import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import SignIn from "@/pages/SignIn";
import Welcome from "@/pages/Welcome";
import LegalPage from "@/pages/LegalPage";
import Dashboard from "@/pages/Dashboard";
import CompanyDetail from "@/pages/CompanyDetail";
import Bookmarks from "@/pages/Bookmarks";
import MyRequests from "@/pages/MyRequests";
import AdminHome from "@/pages/admin/AdminHome";
import AdminCompanies from "@/pages/admin/AdminCompanies";
import AdminCompanyForm from "@/pages/admin/AdminCompanyForm";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminEmailLog from "@/pages/admin/AdminEmailLog";
import AdminMeetings from "@/pages/admin/AdminMeetings";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminFilters from "@/pages/admin/AdminFilters";
import AdminPendingEdits from "@/pages/admin/AdminPendingEdits";
import AdminCompanyDocuments from "@/pages/admin/AdminCompanyDocuments";
import AdminInvestorFirms from "@/pages/admin/AdminInvestorFirms";
import AdminPendingApprovals from "@/pages/admin/AdminPendingApprovals";
import AdminDisclaimers from "@/pages/admin/AdminDisclaimers";
import AdminCompliance from "@/pages/admin/AdminCompliance";
import AdminUserAudit from "@/pages/admin/AdminUserAudit";
import AppShell from "@/components/layout/AppShell";
import InvestorDeclarationModal from "@/components/compliance/InvestorDeclarationModal";
import AccessGate from "@/components/compliance/AccessGate";
import "@/App.css";

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/signin" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/signin" element={<SignIn />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/w/:code" element={<Welcome />} />
            <Route path="/privacy" element={<LegalPage slot="privacy_policy" />} />
            <Route path="/terms" element={<LegalPage slot="terms_and_conditions" />} />
            <Route path="/copyright" element={<LegalPage slot="copyright_notice" />} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/" element={<AccessGate><Dashboard /></AccessGate>} />
              <Route path="/companies/:id" element={<AccessGate><CompanyDetail /></AccessGate>} />
              <Route path="/watchlist" element={<AccessGate><Bookmarks /></AccessGate>} />
              <Route path="/requests" element={<AccessGate><MyRequests /></AccessGate>} />
              <Route path="/admin" element={<Protected roles={["super_admin","investment_team"]}><AdminHome /></Protected>} />
              <Route path="/admin/compliance" element={<Protected roles={["super_admin","investment_team"]}><AdminCompliance /></Protected>} />
              <Route path="/admin/disclaimers" element={<Protected roles={["super_admin","investment_team"]}><AdminDisclaimers /></Protected>} />
              <Route path="/admin/companies" element={<Protected roles={["super_admin","investment_team"]}><AdminCompanies /></Protected>} />
              <Route path="/admin/companies/new" element={<Protected roles={["super_admin","investment_team"]}><AdminCompanyForm /></Protected>} />
              <Route path="/admin/companies/:id/edit" element={<Protected roles={["super_admin","investment_team","client"]}><AdminCompanyForm /></Protected>} />
              <Route path="/admin/companies/:id/documents" element={<Protected roles={["super_admin","investment_team","client"]}><AdminCompanyDocuments /></Protected>} />
              <Route path="/admin/users" element={<Protected roles={["super_admin","investment_team"]}><AdminUsers /></Protected>} />
              <Route path="/admin/emails" element={<Protected roles={["super_admin","investment_team"]}><AdminEmailLog /></Protected>} />
              <Route path="/admin/users/:id/audit" element={<Protected roles={["super_admin","investment_team"]}><AdminUserAudit /></Protected>} />
              <Route path="/admin/meetings" element={<Protected roles={["super_admin","investment_team"]}><AdminMeetings /></Protected>} />
              <Route path="/admin/enquiries" element={<Protected roles={["super_admin","investment_team"]}><AdminEnquiries /></Protected>} />
              <Route path="/admin/investor-firms" element={<Protected roles={["super_admin","investment_team"]}><AdminInvestorFirms /></Protected>} />
              <Route path="/admin/pending-approvals" element={<Protected roles={["super_admin","investment_team"]}><AdminPendingApprovals /></Protected>} />
              <Route path="/admin/audit" element={<Protected roles={["super_admin"]}><AdminAudit /></Protected>} />
              <Route path="/admin/filters" element={<Protected roles={["super_admin","investment_team"]}><AdminFilters /></Protected>} />
              <Route path="/admin/pending-edits" element={<Protected roles={["super_admin","investment_team"]}><AdminPendingEdits /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <InvestorDeclarationModal />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
